const isHiddenProperty = (property: string, elementSchema: any = {}) => {
    return elementSchema.hidden?.includes(property);
};

/**
 * Data-dependent conditional visibility for a schema-form object property.
 *
 * A property may declare a `visibleWhen` guard that is evaluated against its
 * SIBLING data (the value of the object it lives in) at render time. Unlike the
 * static `hidden` array (which only collapses a property into the "More..."
 * section), an unmet `visibleWhen` EXCLUDES the property from rendering entirely
 * — it is neither shown nor listed under "More...". This is the primitive that
 * lets a form show a field only in a relevant context, e.g. `object_template_id`
 * only when `record_type === "CUSTOM_OBJECT"`, or make two sibling fields
 * mutually exclusive (hide each when the other is set).
 * (operator-task-dashboard-widget-editor-form #3/#7, Margot 2026-09-28.)
 *
 * Shape (mirrors the legacy `fieldAttr.dependee`): a single condition, or an
 * array of conditions that must ALL hold. Each condition is
 *   { field: string, value?: any | any[], isSet?: boolean }
 * - `value`  → visible iff sibling `field` equals `value` (or is one of `value`
 *              when an array is given);
 * - `isSet`  → visible iff sibling `field` is set (true) / unset (false), where
 *              "unset" means undefined, null or empty string.
 * A guard with no recognised key (or no `field`) is treated as met (visible).
 */
export const isVisibleWhenMet = (visibleWhen: any, elementData: any = {}): boolean => {
    if (!visibleWhen) {
        return true;
    }
    const conditions = Array.isArray(visibleWhen) ? visibleWhen : [visibleWhen];
    const data = elementData ?? {};
    return conditions.every((condition) => {
        if (!condition || !condition.field) {
            return true;
        }
        const actual = data[condition.field];
        if (Object.prototype.hasOwnProperty.call(condition, "value")) {
            return Array.isArray(condition.value)
                ? condition.value.includes(actual)
                : actual === condition.value;
        }
        if (Object.prototype.hasOwnProperty.call(condition, "isSet")) {
            const isSet = actual !== undefined && actual !== null && actual !== "";
            return condition.isSet ? isSet : !isSet;
        }
        return true;
    });
};
export const sortPropertiesByOrder = (
    properties = {},
    order: string[] = [],
    isIncluded: (property: string) => boolean
) => {
    return order.reduce((obj, key) => {
        if (isIncluded(key) && properties[key] !== undefined) {
            obj[key] = properties[key];
        }
        return obj;
    }, {});
};
export const sortPropertiesByName = (
    properties = {},
    order: string[] = [],
    isIncluded: (property: string) => boolean
) => {
    return Object.keys(properties)
        .sort()
        .reduce((obj, key) => {
            if (!order.includes(key) && properties[key] !== undefined && isIncluded(key)) {
                obj[key] = properties[key];
            }
            return obj;
        }, {});
};
/**
 * The hidden ("More...") section body filters out const discriminants
 * (form-const-*), which never render a control. This returns only the hidden
 * property entries that will actually render, so the caller can suppress an
 * empty "More..." section when the only hidden property is a const discriminant
 * (e.g. the `type` key on a discriminated-union widget branch) — Margot
 * 2026-09-28, operator-task-dashboard-widget-editor-form #5.
 */
export const getRenderableHiddenEntries = (hiddenProperties: Record<string, any> = {}) =>
    Object.entries(hiddenProperties).filter(
        ([, value]: [string, any]) => !value?.element?.startsWith("form-const-")
    );

export const getShownAndHiddenProperties = (elementSchema: any, elementData: any = {}) => {
    const properties = elementSchema.properties ?? {};
    const isPropertyHidden = (property: string) => isHiddenProperty(property, elementSchema);
    // A property whose `visibleWhen` guard is not met against its sibling data is
    // excluded from BOTH the shown and the hidden ("More...") lists — it does not
    // render at all in the current context.
    const isPropertyVisible = (property: string) =>
        isVisibleWhenMet(properties[property]?.visibleWhen, elementData);

    const shownProperties = {
        ...sortPropertiesByOrder(
            properties,
            elementSchema.order,
            (property) => isPropertyVisible(property) && !isPropertyHidden(property)
        ),
        ...sortPropertiesByName(
            properties,
            elementSchema.order,
            (property) => isPropertyVisible(property) && !isPropertyHidden(property)
        ),
    };

    const hiddenProperties = {
        ...sortPropertiesByOrder(properties, elementSchema.order, (property) =>
            Boolean(isPropertyVisible(property) && isPropertyHidden(property))
        ),
        ...sortPropertiesByName(properties, elementSchema.order, (property) =>
            Boolean(isPropertyVisible(property) && isPropertyHidden(property))
        ),
    };

    return { shownProperties, hiddenProperties };
};
